import { useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// ============================================================================
// Types
// ============================================================================

interface CallCardStatusBannerProps {
  // Form state
  isMdt?: boolean;
  isEditMode?: boolean;
  callCardNotClosed?: boolean;
  isEditingClosedCallCard?: boolean;
  // Permissions
  hasEditClosedCallPermission?: boolean;
  hasReopenClosedCallPermission?: boolean;
  canEditForm?: boolean;
  // Assignment state
  isSelfOnCall?: boolean;
  canSelfAssign?: boolean;
  // Event callbacks
  onEnableEditMode?: () => void;
  onSelfAssign?: () => void;
  onEditClosedCall?: () => void;
  onReopenCall?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardStatusBanner({
  isMdt = false,
  isEditMode = false,
  callCardNotClosed = true,
  isEditingClosedCallCard = false,
  hasEditClosedCallPermission = false,
  hasReopenClosedCallPermission = false,
  canEditForm = true,
  isSelfOnCall = false,
  canSelfAssign = false,
  onEnableEditMode,
  onSelfAssign,
  onEditClosedCall,
  onReopenCall,
}: CallCardStatusBannerProps) {
  const { theme } = useTheme();

  // Determine which banner state to show (priority order)
  const bannerState = useMemo((): string => {
    // 1. Call is closed (highest priority)
    if (!callCardNotClosed && !isEditingClosedCallCard) {
      return 'closed';
    }
    // 2. MDT not in edit mode
    if (isMdt && !isEditMode) {
      return 'view-only';
    }
    // 3. Not assigned to call (MDT only, when it affects permissions)
    if (isMdt && !isSelfOnCall && canSelfAssign) {
      return 'not-assigned';
    }
    // 4. Currently editing (show confirmation)
    if (isMdt && isEditMode) {
      return 'editing';
    }
    // 5. Editing closed call (show warning)
    if (isEditingClosedCallCard) {
      return 'editing-closed';
    }
    // 6. No banner needed
    return 'none';
  }, [callCardNotClosed, isEditingClosedCallCard, isMdt, isEditMode, isSelfOnCall, canSelfAssign]);

  const showBanner = useMemo(() => {
    return bannerState !== 'none';
  }, [bannerState]);

  const bannerIcon = useMemo((): string => {
    switch (bannerState) {
      case 'closed': return 'fa-lock';
      case 'view-only': return 'fa-eye';
      case 'not-assigned': return 'fa-user-plus';
      case 'editing': return 'fa-pencil-alt';
      case 'editing-closed': return 'fa-exclamation-triangle';
      default: return '';
    }
  }, [bannerState]);

  const bannerTitle = useMemo((): string => {
    switch (bannerState) {
      case 'closed': return 'CALL CLOSED';
      case 'view-only': return 'VIEW ONLY';
      case 'not-assigned': return 'NOT ASSIGNED';
      case 'editing': return 'EDITING';
      case 'editing-closed': return 'EDITING CLOSED CALL';
      default: return '';
    }
  }, [bannerState]);

  const bannerMessage = useMemo((): string => {
    switch (bannerState) {
      case 'closed':
        // MDT cannot edit/reopen closed calls - must use Dispatch
        if (isMdt) {
          return 'This call is closed. Use Dispatch module to edit or reopen.';
        }
        if (hasEditClosedCallPermission && hasReopenClosedCallPermission) {
          return 'This call is closed.';
        } else if (hasEditClosedCallPermission) {
          return 'This call is closed. Tap to enable editing.';
        } else if (hasReopenClosedCallPermission) {
          return 'This call is closed. Tap to reopen it.';
        }
        return 'This call is closed and cannot be modified.';
      case 'view-only':
        return 'Tap the edit button in the toolbar to make changes.';
      case 'not-assigned':
        return 'Assign yourself to this call to enable more actions.';
      case 'editing':
        return 'You can make changes to this call.';
      case 'editing-closed':
        return 'You are making changes to a closed call.';
      default:
        return '';
    }
  }, [bannerState, isMdt, hasEditClosedCallPermission, hasReopenClosedCallPermission]);

  // For non-closed states, use single action button
  const showActionButton = useMemo((): boolean => {
    switch (bannerState) {
      case 'closed': return false; // Closed state uses individual buttons
      case 'view-only': return true;
      case 'not-assigned': return canSelfAssign;
      default: return false;
    }
  }, [bannerState, canSelfAssign]);

  const actionButtonText = useMemo((): string => {
    switch (bannerState) {
      case 'view-only': return 'Edit';
      case 'not-assigned': return 'Join Call';
      default: return '';
    }
  }, [bannerState]);

  // For closed state, show individual buttons (not on MDT - must use Dispatch)
  const showEditClosedButton = useMemo((): boolean => {
    return bannerState === 'closed' && hasEditClosedCallPermission && !isMdt;
  }, [bannerState, hasEditClosedCallPermission, isMdt]);

  const showReopenButton = useMemo((): boolean => {
    return bannerState === 'closed' && hasReopenClosedCallPermission && !isMdt;
  }, [bannerState, hasReopenClosedCallPermission, isMdt]);

  const hasAnyActionButton = useMemo((): boolean => {
    return showEditClosedButton || showReopenButton || showActionButton;
  }, [showEditClosedButton, showReopenButton, showActionButton]);

  const bannerClass = useMemo((): string => {
    const classes = [`banner-${bannerState}`];
    if (!hasAnyActionButton) {
      classes.push('banner-no-actions');
    }
    return classes.join(' ');
  }, [bannerState, hasAnyActionButton]);

  const onActionClick = useCallback(() => {
    switch (bannerState) {
      case 'view-only':
        onEnableEditMode?.();
        break;
      case 'not-assigned':
        onSelfAssign?.();
        break;
    }
  }, [bannerState, onEnableEditMode, onSelfAssign]);

  if (!showBanner) return null;

  return (
    <div className={`callcard-status-banner ${bannerClass} ${theme}`}>
      <div className="banner-content">
        <div className="banner-info">
          <i className={`fas ${bannerIcon}`}></i>
          <span className="banner-title">{bannerTitle}</span>
          <span className="banner-message">{bannerMessage}</span>
        </div>
        {hasAnyActionButton && (
          <div className="banner-actions">
            {showEditClosedButton && (
              <button
                type="button"
                className="banner-action-btn"
                onClick={() => onEditClosedCall?.()}
              >
                Edit Closed Call
              </button>
            )}
            {showReopenButton && (
              <button
                type="button"
                className="banner-action-btn banner-action-btn--secondary"
                onClick={() => onReopenCall?.()}
              >
                Reopen Call
              </button>
            )}
            {showActionButton && (
              <button
                type="button"
                className="banner-action-btn"
                onClick={onActionClick}
              >
                {actionButtonText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

(window as any).CallCardStatusBannerReact = CallCardStatusBanner;
